import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { OrderStatusEnum } from '../../lib/enums/order-status.enum';
import { OrderTypeEnum } from '../../lib/enums/order-type.enum';
import { Order } from '../../lib/models/trading/types';
import { getSummary } from '../../lib/store/ams/index';
import { getAccountCloseoutStatus } from '../../lib/store/closeout';
import { getCurrentAccount } from '../../lib/store/crm/helpers';
import { RootState } from '../../lib/store/index';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../lib/store/reporting/constants';
import { getAccountTradeHistory, getOpenPositions, setHasOrderUpdate } from '../../lib/store/reporting/index';
import { OpenPosition } from '../../lib/store/reporting/types';
import { resetRequestStatus } from '../../lib/store/trading/index';
import MarketStateCache from '../../lib/store-util/MarketStateCache';
import SubscribeGroupCache from '../../lib/store-util/SubscribeGroupCache';
import { isMarketClosed } from '../../lib/util/MarketDataHelpers';
import { hasInsufficientPositionQuantity, shouldFetchCloseoutStatus } from '../../lib/util/trading-position-helpers';
import { isMessageSummaryAffectingStatus } from '../../lib/util/trading-status-helpers';
import { convertOrderSideToText } from '../../lib/util/TradingHelpers';

import useNewOrderTimer from '../OrderActions/useNewOrderTimer';

import OrderResultModal from './OrderResultModal';

const OrderResponseManager = () => {
  const [showResultModifyModal, setShowResultModifyModal] = useState<boolean>(false);
  const [showResultModalCancel, setShowResultModalCancel] = useState<boolean>(false);
  const [notEnoughStocks, setNotEnoughStocks] = useState<boolean>(false);
  const submittedOrder = useSelector<RootState, Order>((state: RootState) => state.trading.order);
  const requestStatus = useSelector<RootState, OrderStatusEnum>((state: RootState) => state.trading.requestStatus);
  const openPositions = useSelector<RootState, OpenPosition[]>((state: RootState) => state.reporting.openPositions);
  const accountReferenceId = useSelector<RootState, string>((state: RootState) => getCurrentAccount(state));

  const { status, ordType, side: ordSide, orderQty, filledSoFar, symbol } = submittedOrder;
  const showResultModal = [OrderStatusEnum.Accepted, OrderStatusEnum.Rejected].includes(requestStatus);
  const dispatch = useDispatch();

  const { marketState } = MarketStateCache.use();
  const marketClosed = isMarketClosed(marketState);

  const {
    showFillOrder, hasNoResponse, orderTimeoutPassed, showExpired,
  } = useNewOrderTimer(showResultModifyModal && showResultModal, OrderTypeEnum.Limit, status, requestStatus);

  const orderSideAsText = ordSide ? convertOrderSideToText(ordSide) : '';
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;
  const partiallyRejectedOrder = status === OrderStatusEnum.PartiallyReject;
  const finalRejectedOrder = requestStatus === OrderStatusEnum.FinalReject;

  const showModifyOrderModal = (orderTimeoutPassed || (hasNoResponse && showResultModifyModal)
  || (finalRejectedOrder && showResultModifyModal));

  if (sessionStorage.getItem('showUseDetailsCancelModal') === 'true') {
    sessionStorage.removeItem('showUseDetailsCancelModal');
    document.body.classList.add('modal-open');
    setShowResultModalCancel(true);
  }

  useEffect(() => {
    if (sessionStorage.getItem('showUseDetailsEndModal') === 'true') {
      sessionStorage.removeItem('showUseDetailsEndModal');

      const position = openPositions.find((item: OpenPosition) => item.symbol === symbol);

      if (hasInsufficientPositionQuantity(position, { side: ordSide, orderQty })) {
        setNotEnoughStocks(true);
      }

      document.body.classList.add('modal-open');
      setShowResultModifyModal(true);
    }
  }, [openPositions, ordSide, orderQty, symbol]);

  const onCloseOrderResultModal = () => {
    if (orderInProgress) {
      dispatch(resetRequestStatus());
    }
    dispatch(
      getAccountTradeHistory({ tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS, isPendingOrdersCall: true }),
    );
  };

  const onCloseModifyResultModal = () => {
    onCloseOrderResultModal();
    if (!marketClosed) {
      dispatch(getOpenPositions());

      if (shouldFetchCloseoutStatus(
        openPositions,
        { symbol: symbol ?? '', side: ordSide, orderQty, status },
        accountReferenceId,
      )) {
        dispatch(getAccountCloseoutStatus(accountReferenceId));
      }
      if (isMessageSummaryAffectingStatus(status)) {
        dispatch(getSummary());
      }
    }
    SubscribeGroupCache.setComponent('initial');
    setShowResultModifyModal(false);
    dispatch(setHasOrderUpdate(false));
  };

  const onCloseCancelResultModal = () => {
    onCloseOrderResultModal();

    setShowResultModalCancel(false);
    if (isMessageSummaryAffectingStatus(status)) {
      dispatch(getSummary());
    }
    SubscribeGroupCache.setComponent('initial');
  };

  return (
    <>
      {showResultModifyModal && (
        <OrderResultModal
          closeModal={onCloseModifyResultModal}
          notEnoughStocks={notEnoughStocks}
          isModify
          symbol={symbol || ''}
        />
      )}
      {showResultModalCancel && (
      <OrderResultModal
        closeModal={onCloseCancelResultModal}
        notEnoughStocks={notEnoughStocks}
        symbol={symbol || ''}
      />
      )}
    </>
  );
};

export default OrderResponseManager;
